import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Editor } from "@tinymce/tinymce-react";
import { useDispatch } from "react-redux";
import { showAlert } from "../../reducers/alertSlice";
import { httpRequest, onRequestError, printLog } from "../../helper/global";
import { useNavigate } from "react-router-dom";

import {
    Alert,
    Avatar,
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import TinyMCE from "../TinyMCE/TinyMCE";
import Checkbox from "@mui/material/Checkbox";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";

export default function Email({
    theme,
    user,
    permissionConfig,
    isOpen = false,
    onClose = null,
    initialSubject = "",
    initialContent = "",
    initialRecipient = [],
    updateEmailResults = null,
}) {
    const dispatch = useDispatch();

    // Autocomplete for recipients

    // Helper to get email label
    const getEmailLabel = (option) => {
        return option.name
            ? `${option.name} <${option.emailAddress}>`
            : option.emailAddress;
    };

    // Handle recipient change, prevent duplicates
    const handleRecipientChange = (event, value) => {
        if (value.length > 0) {
            const lastValue = value[value.length - 1];
            if (
                lastValue.emailAddress.toLowerCase() === "send to all" ||
                lastValue.emailAddress.toLowerCase() === "send to members" ||
                lastValue.emailAddress.toLowerCase() ===
                    "send to non-members" ||
                lastValue.emailAddress.toLowerCase() ===
                    "send to inactive members"
            ) {
                value = [lastValue];
            } else {
                const sendToAll = value.find((email) => {
                    return email.emailAddress.toLowerCase() === "send to all";
                });
                const sendToMembers = value.find((email) => {
                    return (
                        email.emailAddress.toLowerCase() === "send to members"
                    );
                });
                const sendToNonMembers = value.find((email) => {
                    return (
                        email.emailAddress.toLowerCase() ===
                        "send to non-members"
                    );
                });
                const sendToInactiveMembers = value.find((email) => {
                    return (
                        email.emailAddress.toLowerCase() ===
                        "send to inactive members"
                    );
                });
                if (
                    (sendToAll && value.length > 1) ||
                    (sendToMembers && value.length > 1) ||
                    (sendToNonMembers && value.length > 1) ||
                    (sendToInactiveMembers && value.length > 1)
                ) {
                    value = [lastValue];
                }
            }
        }

        // Remove duplicates by email
        const unique = [];
        const emails = new Set();
        for (const r of value) {
            if (!emails.has(r.emailAddress)) {
                emails.add(r.emailAddress);
                unique.push(r);
            }
        }
        setRecipient(unique);
    };
    const navigate = useNavigate();

    const [emailLists, setEmailLists] = useState([]);
    const [subject, setSubject] = useState(initialSubject);
    const [content, setContent] = useState(initialContent);
    const [recipient, setRecipient] = useState(initialRecipient);

    //addresses dropped from a preloaded list because they unsubscribed after
    //that email was originally sent
    const [excludedRecipients, setExcludedRecipients] = useState([]);
    const [templateId, setTemplateId] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);

    // State for checkboxes
    const [includeHeader, setIncludeHeader] = useState(false);
    const [includeFooter, setIncludeFooter] = useState(false);

    // refs & state to compute remaining editor height
    const panelRef = useRef(null); // ref for the inner modal panel
    const headerRef = useRef(null); // ref for the top controls row
    const [editorHeight, setEditorHeight] = useState(480); // default fallback

    // compute editor height on open and on window resize
    useEffect(() => {
        const computeHeight = () => {
            try {
                const top = panelRef.current
                    ? panelRef.current.getBoundingClientRect().top
                    : 0;
                const headerH = headerRef.current
                    ? headerRef.current.getBoundingClientRect().height
                    : 0;

                // bottom padding used in panel sx p: { xs:1, lg:3 } ~ ~24px, plus some buffer
                const bottomBuffer = 175;

                const remaining = Math.max(
                    300,
                    Math.floor(
                        window.innerHeight - top - headerH - bottomBuffer,
                    ),
                );

                setEditorHeight(remaining);
            } catch (e) {
                // fallback, keep existing height
                setEditorHeight(480);
            }
        };

        if (isOpen) {
            // compute once when opened
            computeHeight();
        }

        // recompute on resize while open
        window.addEventListener("resize", computeHeight);
        return () => window.removeEventListener("resize", computeHeight);
    }, [isOpen]);

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const result = await httpRequest(
                `email/list/true`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }

            setEmailTemplates(result.payload);
        } catch (error) {
            console.error("Error fetching email logs:", error);
        }
    };

    const handleSend = useCallback(async () => {
        // handle save logic here

        if (!recipient || recipient.length === 0) {
            dispatch(
                showAlert({
                    title: "",
                    message: `Please select recipient(s).`,
                }),
            );

            return;
        }

        if (!subject.trim()) {
            dispatch(
                showAlert({
                    title: "",
                    message: `Please provide a subject.`,
                }),
            );

            return;
        }

        const copyEmailList = [...emailLists].filter((email) => {
            return (
                email.emailAddress.toLowerCase() !== "send to all" &&
                email.emailAddress.toLowerCase() !== "send to members" &&
                email.emailAddress.toLowerCase() !== "send to non-members" &&
                email.emailAddress.toLowerCase() !== "send to inactive members"
            );
        });

        const sendToAll = recipient.find((email) => {
            return email.emailAddress.toLowerCase() === "send to all";
        });

        const sendToMembers = recipient.find((email) => {
            return email.emailAddress.toLowerCase() === "send to members";
        });

        const sendToNonMembers = recipient.find((email) => {
            return email.emailAddress.toLowerCase() === "send to non-members";
        });

        const sendToInactiveMembers = recipient.find((email) => {
            return (
                email.emailAddress.toLowerCase() === "send to inactive members"
            );
        });

        let sendRecipient = [...recipient];

        if (sendToAll) {
            sendRecipient = [...copyEmailList].filter((email) => {
                return email.status == "Active";
            });
        } else if (sendToMembers) {
            sendRecipient = [...copyEmailList].filter((email) => {
                return email.member == 1 && email.status == "Active";
            });
        } else if (sendToNonMembers) {
            sendRecipient = [...copyEmailList].filter((email) => {
                return email.member == 0 && email.status == "Active";
            });
        } else if (sendToInactiveMembers) {
            sendRecipient = [...copyEmailList].filter((email) => {
                return email.status == "Inactive";
            });
        }

        // Split recipients into chunks of 20
        const chunkSize = 15;
        const recipientChunks = [];
        for (let i = 0; i < sendRecipient.length; i += chunkSize) {
            recipientChunks.push(sendRecipient.slice(i, i + chunkSize));
        }

        try {
            let count = 0;
            let successEmails = [];

            for (let i = 0; i < recipientChunks.length; i++) {
                const chunk = recipientChunks[i];
                count += chunk.length;
                const postData = {
                    subject: subject.trim(),
                    content: content.replace(/style=/gi, "$style="),
                    recipient: chunk.map((r) => r.emailAddress),
                    includeHeader: includeHeader,
                    includeFooter: includeFooter,
                };

                dispatch(
                    showLoader({
                        message: `Sending ${count} / ${sendRecipient.length} emails...`,
                    }),
                );

                const result = await httpRequest(
                    `email/send`,
                    postData,
                    "POST",
                    (error) => {
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                if (result === false) {
                    return;
                }

                successEmails = [...successEmails, ...postData.recipient];
            }

            if (successEmails.length > 0) {
                //after sending all email, create log and update results
                const postData = {
                    subject: subject.trim(),
                    content: content.replace(/style=/gi, "$style="),
                    recipient: successEmails,
                    includeHeader: includeHeader,
                    includeFooter: includeFooter,
                    createLog: true,
                };

                dispatch(
                    showLoader({
                        message: `Sending ${count} / ${sendRecipient.length} emails...`,
                    }),
                );

                const result = await httpRequest(
                    `email/send`,
                    postData,
                    "POST",
                    (error) => {
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                if (result === false) {
                    return;
                }

                if (updateEmailResults) {
                    updateEmailResults(result.payload);
                }

                dispatch(showAlert({ message: "Email sent successfully!" }));
            }

            dispatch(hideLoader());
            handleClose();
        } catch (error) {
            console.error("Error sending emails:", error);
            dispatch(hideLoader());
        }
    }, [
        recipient,
        subject,
        content,
        includeHeader,
        includeFooter,
        emailLists,
        dispatch,
    ]);

    const fetchEmailLists = async () => {
        try {
            const result = await httpRequest(
                `email/emailAddress`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }

            const emailResult = [...result.payload];

            emailResult.unshift({ emailAddress: "Send to Inactive Members" });
            emailResult.unshift({ emailAddress: "Send to Non-members" });
            emailResult.unshift({ emailAddress: "Send to Members" });
            emailResult.unshift({ emailAddress: "Send to All" });

            setEmailLists(emailResult);
        } catch (error) {
            console.error("Error fetching email lists:", error);
        }
    };

    const handleEmailTemplateChange = (event) => {
        setTemplateId(event.target.value);
        const selectedTemplate = emailTemplates.find(
            (template) => template.templateId === event.target.value,
        );
        if (selectedTemplate) {
            setSubject(selectedTemplate.title);
            setContent(selectedTemplate.content);
        } else {
            setSubject("");
            setContent("");
        }
    };

    useEffect(() => {
        setSubject(initialSubject);
        setContent(initialContent);
        setTemplateId(null);

        if (isOpen) {
            fetchEmailLists();
            fetchEmailTemplates();
        }
    }, [isOpen, initialSubject, initialContent, initialRecipient]);

    useEffect(() => {
        const loadRecipients = async () => {
            const tempEmailName = {};
            [...emailLists].forEach((item) => {
                tempEmailName[item.emailAddress] = item.name ?? "";
            });

            let addresses = [...initialRecipient];

            //A resend replays the recipient list stored on the original email
            //log, which is a snapshot. Anyone who unsubscribed since then is
            //still in it. The backend drops them at send time either way, but
            //without this the compose screen shows them as recipients and the
            //sent count silently disagrees with reality.
            if (addresses.length > 0) {
                const result = await httpRequest(
                    `email/checkOptOut`,
                    { emails: addresses },
                    "POST",
                    () => {},
                );

                if (result && result.payload && result.payload.optedOut) {
                    const blocked = new Set(
                        result.payload.optedOut.map((e) => e.toLowerCase()),
                    );

                    const removed = addresses.filter((a) =>
                        blocked.has(String(a).toLowerCase()),
                    );

                    addresses = addresses.filter(
                        (a) => !blocked.has(String(a).toLowerCase()),
                    );

                    setExcludedRecipients(removed);
                } else {
                    setExcludedRecipients([]);
                }
            } else {
                setExcludedRecipients([]);
            }

            setRecipient(
                addresses.map((r) => {
                    return { name: tempEmailName[r] ?? "", emailAddress: r };
                }),
            );
        };

        loadRecipients();
    }, [emailLists]);

    return (
        <>
            {isOpen && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                        zIndex: 1200,
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        display: `${isOpen ? "flex" : "none"}`,
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Box
                        ref={panelRef} // <-- attach panel ref
                        sx={{
                            backgroundColor: theme.container.backgroundColor,
                            borderRadius: 2,
                            boxShadow: 24,
                            maxWidth: { xs: "100%", md: "95%", xl: "85%" },
                            width: "100%",
                            height: { xs: "100%", md: "97%", xl: "97%" },
                            overflowY: "auto",
                            p: { xs: 1, lg: 3 },
                        }}
                    >
                        <Box
                            ref={headerRef} // <-- attach header ref used to subtract height
                            display={"flex"}
                            justifyContent="flex-end"
                            alignItems={"center"}
                            mb={0}
                            gap={1}
                        >
                            <FormControl fullWidth size="small">
                                <InputLabel id="email-template">
                                    Email Template
                                </InputLabel>
                                <Select
                                    labelId="email-template"
                                    id="email-template"
                                    value={templateId || ""}
                                    label="Email Template"
                                    onChange={handleEmailTemplateChange}
                                >
                                    {emailTemplates.map((template) => (
                                        <MenuItem
                                            key={template.templateId}
                                            value={template.templateId}
                                        >
                                            {template.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* <FormControlLabel
                                size="small"
                                control={
                                    <Checkbox
                                        checked={includeHeader}
                                        onChange={(e) => {
                                            setIncludeHeader(e.target.checked);
                                        }}
                                    />
                                }
                                label="Header"
                            />
                            <FormControlLabel
                                size="small"
                                control={
                                    <Checkbox
                                        checked={includeFooter}
                                        onChange={(e) =>
                                            setIncludeFooter(e.target.checked)
                                        }
                                    />
                                }
                                label="Footer"
                            /> */}
                            <Button
                                size="small"
                                onClick={handleClose}
                                variant="contained"
                                color="primary"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                onClick={handleSend}
                                color="primary"
                                variant="contained"
                            >
                                Send
                            </Button>
                        </Box>

                        <Autocomplete
                            size="small"
                            multiple
                            options={emailLists}
                            getOptionLabel={getEmailLabel}
                            value={recipient}
                            onChange={handleRecipientChange}
                            limitTags={5}
                            sx={{
                                // align clear & popup icons to top of the input
                                "& .MuiAutocomplete-endAdornment": {
                                    alignItems: "flex-start",
                                    paddingTop: "6px",
                                },
                                // align input area to top so chips & text start from top
                                "& .MuiAutocomplete-inputRoot": {
                                    alignItems: "flex-start",
                                    paddingTop: "6px",
                                },
                                // small adjustment for rendered chips
                                "& .MuiChip-root": {
                                    marginTop: "4px",
                                },
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        size="small"
                                        label={getEmailLabel(option)}
                                        {...getTagProps({ index })}
                                        key={option.emailAddress}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    size="small"
                                    {...params}
                                    variant="outlined"
                                    label="Recipients"
                                    placeholder="Add recipients"
                                    margin="normal"
                                />
                            )}
                            fullWidth
                        />

                        {excludedRecipients.length > 0 && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {`${excludedRecipients.length} recipient${
                                    excludedRecipients.length === 1 ? "" : "s"
                                } removed — unsubscribed since this email was
                                last sent.`}
                            </Alert>
                        )}

                        <TextField
                            size="small"
                            label="Subject"
                            fullWidth
                            margin="normal"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            sx={{ mt: 0 }}
                        />
                        <Box
                            sx={{
                                // use the computed editorHeight
                                height: `${editorHeight}px`,
                            }}
                        >
                            <TinyMCE
                                theme={theme}
                                initialContent={content}
                                onContentChange={setContent}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
}
